import React from 'react';

/**
 * Editable heading — shows an input in edit mode, plain text
 * otherwise. The non-editing span carries `id={headingKey}` and
 * `scroll-margin-top` so the TOC's `<a href="#sectionId">` anchors
 * land on the section heading with clearance under any sticky chrome.
 *
 * Edit mode skips the id (the input element gets keyboard focus, not
 * a hash-link target) so a stale anchor click during editing doesn't
 * fight with the active text caret.
 *
 * `inline` mode (SLK-H Prompt 4) suppresses the inner `<span id>` and
 * inline `scroll-margin-top` so an OUTER `<section id={sectionId}>`
 * (e.g. SleekSection) can own the anchor target without duplicating
 * the id in the DOM. Edit mode is unchanged (input always renders).
 */
const EditableHeading = React.memo(({ headingKey, defaultText, className, isEditing, overrides, onChange, inline }: {
  headingKey: string; defaultText: string; className?: string;
  isEditing: boolean; overrides: Record<string, string>;
  onChange: (key: string, value: string) => void;
  inline?: boolean;
}) => {
  const text = overrides[headingKey] || defaultText;
  if (isEditing) {
    return <input className={`${className} bg-transparent border-b-2 border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary w-full`}
      value={text} onChange={(e) => onChange(headingKey, e.target.value)} />;
  }
  if (inline) {
    return <span className={className}>{text}</span>;
  }
  return (
    <span
      id={headingKey}
      className={className}
      style={{ scrollMarginTop: '80px' }}
    >
      {text}
    </span>
  );
});

EditableHeading.displayName = 'EditableHeading';

export { EditableHeading };
